import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import FadeLoader from 'react-spinners/FadeLoader'
import api from '../api/api'
import { loadStripe } from '@stripe/stripe-js'
import error from '../assets/error.png'
import success from '../assets/success.png'
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaHome, FaShoppingBag, FaEnvelope, FaPhone, FaClock } from 'react-icons/fa'
import { useDispatch } from 'react-redux'
import { clearCart } from '../store/reducers/cardReducer'

const load = async (sellerId) => {
  try {
    if (!sellerId) {
      throw new Error('Seller ID not found');
    }
    // Fetch seller's payment configuration
    const { data } = await api.get(`/seller/${sellerId}/payment-config`);
    if (data.success && data.config.stripe && data.config.stripe.isEnabled) {
      return await loadStripe(data.config.stripe.publishableKey);
    } else {
      throw new Error('Stripe is not configured or enabled for this seller');
    }
  } catch (error) {
    console.error('Error loading Stripe config:', error);
    throw new Error('Failed to load payment configuration');
  }
}

const ConfirmOrder = ({ sellerId, sellerName }) => {
  console.log('🚀 ConfirmOrder component mounted with props:', { sellerId, sellerName })
  console.log('🚀 Props details:', { sellerId, sellerName })
  
  const dispatch = useDispatch()
  const [loader, setLoader] = useState(true)
  const [stripe, setStripe] = useState('')
  const [message, setMessage] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [orderNumber, setOrderNumber] = useState(null)

  const update_payment = async (orderNumber) => {
    if (!orderNumber) {
      // console.log('No order number provided')
      setErrorMessage('Order number is missing')
      setLoader(false)
      return
    }

    try {
      // console.log('Updating payment for order:', orderNumber)
      
      // Determine payment method from URL parameters.
      // Do not default to Stripe when no method is provided.
      const paymentMethod = new URLSearchParams(window.location.search).get('payment_method') || 'NA'
      
      console.log('Updating payment with method:', {
        orderNumber,
        paymentMethod,
        urlParams: window.location.search,
        extractedPaymentMethod: new URLSearchParams(window.location.search).get('payment_method')
      });
      
      const { data } = await api.put(`/order/order-confirm/${orderNumber}`, {
        paymentMethod: paymentMethod // Send the correct payment method
      })
      // console.log('Payment update response:', data)
      
      if (data.success) {
        localStorage.removeItem('orderNumber')
        // Clear cart state after successful payment
        dispatch(clearCart())
        // Redirect to the seller's dashboard orders page instead of non-existent /payment-success
        window.location.href = `/${sellerName}/dashboard/my-orders`
      } else {
        throw new Error(data.message || 'Failed to confirm order')
      }
    } catch (error) {
      // console.error('Error updating payment:', error)
      setErrorMessage(error.response?.data?.message || 'Failed to confirm order')
      setLoader(false)
    }
  }

  const update_payment_with_details = async (paymentDetails, orderNumberParam = null) => {
    const orderNumber = orderNumberParam || localStorage.getItem('orderNumber')
    if (!orderNumber) {
      setErrorMessage('Order number is missing')
      setLoader(false)
      return
    }

    try {
      // Determine payment method from URL parameters or payment details gateway.
      // Do not default to Stripe when no method is provided.
      const paymentMethod = new URLSearchParams(window.location.search).get('payment_method') || 
                           paymentDetails.gateway || 'NA'
      
      const { data } = await api.put(`/order/order-confirm/${orderNumber}`, {
        paymentMethod: paymentMethod,
        paymentDetails: paymentDetails
      })
      
      if (data.success) {
        localStorage.removeItem('orderNumber')
        // Clear cart state after successful payment
        dispatch(clearCart())
        // Redirect to the seller's dashboard orders page
        window.location.href = `/${sellerName}/dashboard/my-orders`
      } else {
        throw new Error(data.message || 'Failed to confirm order')
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to confirm order')
      setLoader(false)
    }
  }

  useEffect(() => {
    const handlePaymentConfirmation = async () => {
      try {
        console.log('🔄 ConfirmOrder: Starting payment confirmation handling...')
        const urlParams = new URLSearchParams(window.location.search)
        console.log('🔍 ConfirmOrder: URL parameters:', Object.fromEntries(urlParams))
        console.log('🔍 URL params details:', {
          paymentMethod: urlParams.get('payment_method'),
          paymentStatus: urlParams.get('status'),
          orderNumber: urlParams.get('orderNumber'),
          errorMessage: urlParams.get('error_message')
        })
        
        // Check for payment reference in URL (for offline payments)
        const paymentReference = urlParams.get('payment_reference')
        if (paymentReference) {
          // console.log('Found offline payment reference:', paymentReference)
          // Extract order number from payment reference (format: OFF-{orderNumber}-{timestamp})
          const orderNumber = paymentReference.split('-')[1]
          if (orderNumber) {
            // console.log('Extracted order number from reference:', orderNumber)
            setMessage('pending')
            setOrderNumber(orderNumber)
            setLoader(false) // Prevent timeout error from overwriting pending state
            // For offline payments, do NOT call update_payment_with_details or try to mark as paid
            return
          }
        }

        // Check for PayPal and PayFast payment parameters
        const paymentMethod = new URLSearchParams(window.location.search).get('payment_method')
        const paymentStatus = new URLSearchParams(window.location.search).get('status')
        const errorMessage = new URLSearchParams(window.location.search).get('error')
        const orderNumberFromUrl = new URLSearchParams(window.location.search).get('orderNumber')
        
        if (paymentMethod === 'paypal') {
          let orderNumber = localStorage.getItem('orderNumber')
          
          // Fallback: try to extract order number from URL path if not in localStorage
          if (!orderNumber) {
            const pathParts = window.location.pathname.split('/')
            // URL format: /{sellerId}/order/confirm
            // We need to get the order number from somewhere else
            // For now, we'll show an error if order number is not available
            setMessage('failed')
            setErrorMessage('Order information not found. Please contact support.')
            setLoader(false)
            return
          }
          
          if (orderNumber && paymentStatus === 'success') {
            setMessage('succeeded')
            update_payment(orderNumber)
            return
          } else if (paymentStatus === 'error') {
            setMessage('failed')
            setErrorMessage(errorMessage ? decodeURIComponent(errorMessage) : 'PayPal payment failed')
            setLoader(false)
            return
          }
        } else if (paymentMethod === 'payfast') {
          // Handle PayFast payment confirmation
          let orderNumber = orderNumberFromUrl || localStorage.getItem('orderNumber')
          
          console.log('PayFast payment confirmation:', {
            paymentMethod,
            paymentStatus,
            orderNumber,
            orderNumberFromUrl,
            localStorageOrderNumber: localStorage.getItem('orderNumber')
          });
          
          if (orderNumber && paymentStatus === 'success') {
            setMessage('succeeded')
            update_payment(orderNumber)
            return
          } else if (paymentStatus === 'cancelled') {
            setMessage('failed')
            setErrorMessage('PayFast payment was cancelled')
            setLoader(false)
            return
          } else if (paymentStatus === 'error') {
            setMessage('failed')
            setErrorMessage(errorMessage ? decodeURIComponent(errorMessage) : 'PayFast payment failed')
            setLoader(false)
            return
          }
        } else if (paymentMethod === 'cybersource') {
          console.log('🎯 ConfirmOrder: Processing CyberSource payment confirmation...')
          // Handle CyberSource payment confirmation
          let orderNumber = orderNumberFromUrl || localStorage.getItem('orderNumber')
          
          // Set the order number from URL to state immediately
          if (orderNumberFromUrl) {
            setOrderNumber(orderNumberFromUrl)
          }
          
          console.log('CyberSource confirmation debug:', {
            paymentMethod,
            paymentStatus,
            orderNumberFromUrl,
            localStorageOrderNumber: localStorage.getItem('orderNumber'),
            cybersourcePayment: localStorage.getItem('cybersourcePayment'),
            allUrlParams: Object.fromEntries(new URLSearchParams(window.location.search))
          });
          console.log('CyberSource debug details:', {
            paymentMethod,
            paymentStatus,
            orderNumberFromUrl,
            localStorageOrderNumber: localStorage.getItem('orderNumber'),
            cybersourcePaymentData: localStorage.getItem('cybersourcePayment') ? JSON.parse(localStorage.getItem('cybersourcePayment')) : null
          });
          
          console.log('CyberSource payment confirmation:', {
            paymentMethod,
            paymentStatus,
            orderNumber,
            orderNumberFromUrl,
            localStorageOrderNumber: localStorage.getItem('orderNumber')
          });
          
          if (orderNumber && paymentStatus === 'success') {
            console.log('✅ CyberSource: Processing successful payment for order:', orderNumber)
            // Get CyberSource payment details from localStorage
            const cybersourcePayment = localStorage.getItem('cybersourcePayment')
            console.log('🔍 CyberSource: Payment data from localStorage:', cybersourcePayment)
            
            if (cybersourcePayment) {
              try {
                const paymentData = JSON.parse(cybersourcePayment)
                console.log('✅ CyberSource: Parsed payment data:', paymentData)
                const paymentDetails = {
                  transactionId: paymentData.transactionId,
                  paymentId: paymentData.transactionId,
                  amount: paymentData.amount,
                  fee: 0, // CyberSource fees are handled separately
                  netAmount: paymentData.amount,
                  gateway: 'cybersource',
                  status: (paymentData.status === 'AUTHORIZED' || paymentData.status === 'AUTHORIZED_PENDING_REVIEW') ? 'completed' : 'pending'
                }
                
                console.log('✅ CyberSource: Calling update_payment_with_details with:', paymentDetails)
                
                // Set message to succeeded first (like Stripe does)
                setMessage('succeeded')
                
                // Update payment with details
                update_payment_with_details(paymentDetails, orderNumber)
                
                // Clean up localStorage
                localStorage.removeItem('cybersourcePayment')
                console.log('✅ CyberSource: Payment processing completed')
                return
              } catch (error) {
                console.error('❌ Error parsing CyberSource payment data:', error)
              }
            } else {
              console.log('⚠️ CyberSource: No payment data found in localStorage')
            }
            
            // Fallback to regular payment update
            console.log('🔄 CyberSource: No payment data in localStorage, using fallback');
            console.log('🔄 CyberSource: Setting message to succeeded and calling update_payment');
            setMessage('succeeded')
            update_payment(orderNumber)
            console.log('🔄 CyberSource: Fallback processing completed')
            return
          } else if (paymentStatus === 'error') {
            console.log('❌ CyberSource: Payment failed, setting error message')
            setMessage('failed')
            setErrorMessage(errorMessage ? decodeURIComponent(errorMessage) : 'CyberSource payment failed')
            setLoader(false)
            return
          } else if (!orderNumber) {
            // No order number found
            console.error('CyberSource: No order number found', {
              orderNumberFromUrl,
              localStorageOrderNumber: localStorage.getItem('orderNumber'),
              allParams: Object.fromEntries(new URLSearchParams(window.location.search))
            });
            setMessage('failed')
            setErrorMessage('Order number not found. Please contact support.')
            setLoader(false)
            return
          } else {
            // Unknown status
            console.log('⚠️ CyberSource: Unknown payment status, setting failed message')
            setMessage('failed')
            setErrorMessage('Payment status unknown. Please contact support.')
            setLoader(false)
            return
          }
        }

        // For Stripe payments
        try {
          const tempStripe = await load(sellerId)
          setStripe(tempStripe)

          const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret')
          if (!clientSecret) {
            // console.log('No payment intent found')
            setMessage('failed')
            setErrorMessage('Payment intent not found')
            setLoader(false)
            return
          }

          const { paymentIntent } = await tempStripe.retrievePaymentIntent(clientSecret)
          // console.log('Payment intent status:', paymentIntent.status)
          switch (paymentIntent.status) {
            case "succeeded":
              setMessage('succeeded')
              // Prepare payment details for Stripe
              const paymentDetails = {
                transactionId: paymentIntent.id,
                paymentId: paymentIntent.id,
                amount: paymentIntent.amount / 100, // Convert from cents
                fee: paymentIntent.application_fee_amount ? paymentIntent.application_fee_amount / 100 : null,
                netAmount: (paymentIntent.amount - (paymentIntent.application_fee_amount || 0)) / 100,
                gateway: 'stripe',
                status: 'completed'
              };
              
              // Update payment with details
              update_payment_with_details(paymentDetails)
              break
            case "processing":
              setMessage('processing')
              break
            case "requires_payment_method":
              setMessage('failed')
              setErrorMessage('Payment method required')
              setLoader(false)
              break
            default:
              setMessage('failed')
              setErrorMessage('Payment failed')
              setLoader(false)
          }
        } catch (stripeError) {
          // If Stripe is not available, check if this is an offline payment
          const orderNumber = localStorage.getItem('orderNumber')
          if (orderNumber) {
            // This might be an offline payment confirmation
            setMessage('succeeded')
            update_payment(orderNumber)
          } else {
            setMessage('failed')
            setErrorMessage('Payment method not available. Please contact support.')
            setLoader(false)
          }
        }
      } catch (error) {
        console.error('❌ Error handling payment confirmation:', error)
        console.error('❌ Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        })
        setMessage('failed')
        setErrorMessage(error.message || 'Failed to process payment')
        setLoader(false)
      }
    }

    handlePaymentConfirmation()
  }, [])

  useEffect(() => {
    if (message === 'succeeded') {
      // For Stripe payments, use order number from localStorage
      const orderNumber = localStorage.getItem('orderNumber')
      if (orderNumber) {
        update_payment(orderNumber)
      }
    } else if (message === 'failed') {
      setLoader(false)
    }
  }, [message])

  // Add a timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loader) {
        console.log('Loading timeout reached - setting fallback state');
        setLoader(false)
        setMessage('failed')
        setErrorMessage('Request timed out')
      }
    }, 10000) // 10 seconds timeout

    return () => clearTimeout(timeout)
  }, [loader])

  // Debug: Log component state changes
  useEffect(() => {
    console.log('ConfirmOrder state changed:', {
      loader,
      message,
      errorMessage,
      orderNumber,
      url: window.location.href,
      sellerId,
      sellerName
    });
  }, [loader, message, errorMessage, orderNumber, sellerId, sellerName])

  // Debug: Log render conditions
  useEffect(() => {
    console.log('ConfirmOrder render conditions:', {
      message,
      loader,
      isFailed: message === 'failed',
      isSucceeded: message === 'succeeded',
      isPending: message === 'pending',
      isNull: message === null,
      isUndefined: message === undefined
    });
  }, [message, loader])

  useEffect(() => {
    // Extract order number from URL first, then localStorage for display
    const urlParams = new URLSearchParams(window.location.search)
    const orderNumberFromUrl = urlParams.get('orderNumber')
    const paymentReference = urlParams.get('payment_reference')
    const storedOrderNumber = localStorage.getItem('orderNumber')
    
    if (orderNumberFromUrl) {
      // Prioritize order number from URL parameters
      setOrderNumber(orderNumberFromUrl)
    } else if (paymentReference) {
      // Extract order number from payment reference (format: OFF-{orderNumber}-{timestamp})
      const orderNumberFromRef = paymentReference.split('-')[1]
      if (orderNumberFromRef) {
        setOrderNumber(orderNumberFromRef)
      }
    } else if (storedOrderNumber) {
      // Fallback to localStorage
      setOrderNumber(storedOrderNumber)
    }
  }, [])

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  console.log('🎨 ConfirmOrder: About to render with state:', { message, loader, errorMessage, orderNumber })
  
  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex justify-center items-center p-4'>
      <div className='max-w-2xl w-full'>
        {message === 'failed' ? (
          <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-red-100'>
            {/* Error Header */}
            <div className='mb-6'>
              <div className='inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4'>
                <FaTimesCircle className='text-red-500 text-4xl' />
              </div>
              <h1 className='text-3xl font-bold text-gray-800 mb-2'>Payment Failed</h1>
              <p className='text-gray-600'>We encountered an issue processing your payment</p>
            </div>

            {/* Error Details */}
            <div className='bg-red-50 rounded-lg p-6 mb-6'>
              <div className='flex items-center justify-center mb-3'>
                <FaTimesCircle className='text-red-500 mr-2' />
                <span className='font-semibold text-red-700'>Error Details</span>
              </div>
              {errorMessage && (
                <p className="text-red-600 text-sm">{errorMessage}</p>
              )}
            </div>

            {/* Help Section */}
            <div className='bg-gray-50 rounded-lg p-6 mb-6'>
              <h3 className='font-semibold text-gray-800 mb-3'>Need Help?</h3>
              <div className='space-y-2 text-sm text-gray-600'>
                <div className='flex items-center justify-center'>
                  <FaEnvelope className='mr-2 text-blue-500' />
                  <span>Contact our support team</span>
                </div>
                <div className='flex items-center justify-center'>
                  <FaPhone className='mr-2 text-blue-500' />
                  <span>Call us for immediate assistance</span>
                </div>
                <div className='flex items-center justify-center'>
                  <FaClock className='mr-2 text-blue-500' />
                  <span>We're here to help 24/7</span>
                </div>
              </div>
            </div>

            {/* Troubleshooting Tips */}
            <div className='bg-yellow-50 rounded-lg p-6 mb-6 border border-yellow-200'>
              <h3 className='font-semibold text-yellow-800 mb-3'>💡 Troubleshooting Tips</h3>
              <div className='space-y-2 text-sm text-yellow-700'>
                <p>• Check if your payment method has sufficient funds</p>
                <p>• Ensure your card details are entered correctly</p>
                <p>• Try using a different payment method</p>
                <p>• Clear your browser cache and try again</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='space-y-3'>
              <Link 
                className='w-full inline-flex items-center justify-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl' 
                to={`/${sellerName}/dashboard/my-orders`}
              >
                <FaShoppingBag className='mr-2' />
                Back to Orders
              </Link>
              <Link 
                className='w-full inline-flex items-center justify-center px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-all duration-200' 
                to={`/${sellerName}`}
              >
                <FaHome className='mr-2' />
                Return to Home
              </Link>
            </div>
          </div>
        ) : message === 'succeeded' ? (
          loader ? (
            <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-blue-100'>
              {/* Loading State */}
              <div className='mb-6'>
                <div className='inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4'>
                  <FaSpinner className='text-blue-500 text-4xl animate-spin' />
                </div>
                <h1 className='text-3xl font-bold text-gray-800 mb-2'>Confirming Your Order</h1>
                <p className='text-gray-600'>Please wait while we process your payment</p>
              </div>
              
              <FadeLoader color="#3B82F6" className='mx-auto' />
              <p className="text-gray-600 mt-6 text-lg">Securing your transaction...</p>
            </div>
          ) : (
            <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-green-100'>
              {/* Success Header */}
              <div className='mb-6'>
                <div className='inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4'>
                  <FaCheckCircle className='text-green-500 text-4xl' />
                </div>
                <h1 className='text-3xl font-bold text-gray-800 mb-2'>Payment Successful!</h1>
                <p className='text-gray-600'>Thank you for your purchase</p>
                <p className='text-green-700 font-semibold mt-2'>Thank you for shopping with {sellerName}!</p>
              </div>

              {/* Order Details */}
              <div className='bg-green-50 rounded-lg p-6 mb-6'>
                <div className='flex items-center justify-center mb-3'>
                  <FaCheckCircle className='text-green-500 mr-2' />
                  <span className='font-semibold text-green-700'>Order Confirmed</span>
                </div>
                {orderNumber && (
                  <p className='text-green-600 font-medium'>Order #{orderNumber}</p>
                )}
                <div className='flex items-center justify-center mt-2 text-sm text-green-600'>
                  <FaClock className='mr-1' />
                  <span>{getCurrentTime()} • {getCurrentDate()}</span>
                </div>
              </div>

              {/* Success Message */}
              <div className='bg-blue-50 rounded-lg p-6 mb-6'>
                <h3 className='font-semibold text-gray-800 mb-3'>What's Next?</h3>
                <div className='space-y-2 text-sm text-gray-600'>
                  <p>✓ Your order has been successfully placed</p>
                  <p>✓ You'll receive an email confirmation shortly</p>
                  <p>✓ Track your order status in your dashboard</p>
                  <p>✓ Our team will process your order within 24 hours</p>
                  <p>✓ You can view your order history anytime</p>
                </div>
              </div>

              {/* Greeting Message */}
              <div className='bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-6 border border-green-200'>
                <h3 className='font-semibold text-gray-800 mb-3'>🎉 Thank You for Your Purchase!</h3>
                <div className='space-y-2 text-sm text-gray-600'>
                  <p>We're excited to fulfill your order and provide you with the best shopping experience.</p>
                  <p>Your satisfaction is our top priority, and we're committed to delivering quality products to your doorstep.</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className='bg-gray-50 rounded-lg p-6 mb-6'>
                <h3 className='font-semibold text-gray-800 mb-3'>Need Assistance?</h3>
                <div className='space-y-2 text-sm text-gray-600'>
                  <div className='flex items-center justify-center'>
                    <FaEnvelope className='mr-2 text-blue-500' />
                    <span>Check your email for order details</span>
                  </div>
                  <div className='flex items-center justify-center'>
                    <FaPhone className='mr-2 text-blue-500' />
                    <span>Contact us if you have questions</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className='space-y-3'>
                <Link 
                  className='w-full inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl' 
                  to={`/${sellerName}/dashboard/my-orders`}
                >
                  <FaShoppingBag className='mr-2' />
                  View My Orders
                </Link>
                <Link 
                  className='w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200' 
                  to={`/${sellerName}`}
                >
                  <FaHome className='mr-2' />
                  Continue Shopping
                </Link>
              </div>
            </div>
          )
        ) : message === 'processing' ? (
          <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-yellow-100'>
            {/* Processing Header */}
            <div className='mb-6'>
              <div className='inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-4'>
                <FaSpinner className='text-yellow-500 text-4xl animate-spin' />
              </div>
              <h1 className='text-3xl font-bold text-gray-800 mb-2'>Processing Payment</h1>
              <p className='text-gray-600'>Your payment is being processed securely</p>
            </div>
            
            <FadeLoader color="#F59E0B" className='mx-auto' />
            <p className="text-gray-600 mt-6 text-lg">This may take a few moments...</p>
            
            <div className='mt-6 bg-yellow-50 rounded-lg p-4'>
              <p className='text-sm text-yellow-700'>
                Please don't close this window while we process your payment
              </p>
            </div>
          </div>
        ) : message === 'pending' ? (
          <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-yellow-100'>
            <div className='mb-6'>
              <div className='inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-4'>
                <FaClock className='text-yellow-500 text-4xl' />
              </div>
              <h1 className='text-3xl font-bold text-gray-800 mb-2'>Payment Pending Approval</h1>
              <p className='text-gray-600'>Your payment is pending seller approval. You will be notified once the seller confirms your payment.</p>
            </div>
            <div className='bg-yellow-50 rounded-lg p-6 mb-6'>
              <div className='flex items-center justify-center mb-3'>
                <FaClock className='text-yellow-500 mr-2' />
                <span className='font-semibold text-yellow-700'>Order Pending</span>
              </div>
              {orderNumber && (
                <p className='text-yellow-600 font-medium'>Order #{orderNumber}</p>
              )}
            </div>
            <div className='space-y-3'>
              <Link 
                className='w-full inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl' 
                to={`/${sellerName}/dashboard/my-orders`}
              >
                <FaShoppingBag className='mr-2' />
                View My Orders
              </Link>
              <Link 
                className='w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200' 
                to={`/${sellerName}`}
              >
                <FaHome className='mr-2' />
                Continue Shopping
              </Link>
            </div>
          </div>
        ) : message === 'succeeded' ? (
          loader ? (
            <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-blue-100'>
              {/* Loading State */}
              <div className='mb-6'>
                <div className='inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4'>
                  <FaSpinner className='text-blue-500 text-4xl animate-spin' />
                </div>
                <h1 className='text-3xl font-bold text-gray-800 mb-2'>Confirming Your Order</h1>
                <p className='text-gray-600'>Please wait while we process your payment</p>
              </div>
              
              <FadeLoader color="#3B82F6" className='mx-auto' />
              <p className="text-gray-600 mt-6 text-lg">Securing your transaction...</p>
            </div>
          ) : (
            <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-green-100'>
              {/* Success Header */}
              <div className='mb-6'>
                <div className='inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4'>
                  <FaCheckCircle className='text-green-500 text-4xl' />
                </div>
                <h1 className='text-3xl font-bold text-gray-800 mb-2'>Payment Successful!</h1>
                <p className='text-gray-600'>Thank you for your purchase</p>
                <p className='text-green-700 font-semibold mt-2'>Thank you for shopping with {sellerName}!</p>
              </div>

              {/* Order Details */}
              <div className='bg-green-50 rounded-lg p-6 mb-6'>
                <div className='flex items-center justify-center mb-3'>
                  <FaCheckCircle className='text-green-500 mr-2' />
                  <span className='font-semibold text-green-700'>Order Confirmed</span>
                </div>
                {orderNumber && (
                  <p className='text-green-600 font-medium'>Order #{orderNumber}</p>
                )}
                <div className='flex items-center justify-center mt-2 text-sm text-green-600'>
                  <FaClock className='mr-1' />
                  <span>{getCurrentTime()} • {getCurrentDate()}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className='space-y-3'>
                <Link 
                  className='w-full inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl' 
                  to={`/${sellerName}/dashboard/my-orders`}
                >
                  <FaShoppingBag className='mr-2' />
                  View My Orders
                </Link>
                <Link 
                  className='w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200' 
                  to={`/${sellerName}`}
                >
                  <FaHome className='mr-2' />
                  Continue Shopping
                </Link>
              </div>
            </div>
          )
        ) : message === 'pending' ? (
          <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-yellow-100'>
            {/* Pending Header */}
            <div className='mb-6'>
              <div className='inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-4'>
                <FaClock className='text-yellow-500 text-4xl' />
              </div>
              <h1 className='text-3xl font-bold text-gray-800 mb-2'>Payment Pending Approval</h1>
              <p className='text-gray-600'>Your payment is pending seller approval. You will be notified once the seller confirms your payment.</p>
            </div>
            <div className='bg-yellow-50 rounded-lg p-6 mb-6'>
              <div className='flex items-center justify-center mb-3'>
                <FaClock className='text-yellow-500 mr-2' />
                <span className='font-semibold text-yellow-700'>Order Pending</span>
              </div>
              {orderNumber && (
                <p className='text-yellow-600 font-medium'>Order #{orderNumber}</p>
              )}
            </div>
            <div className='space-y-3'>
              <Link 
                className='w-full inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl' 
                to={`/${sellerName}/dashboard/my-orders`}
              >
                <FaShoppingBag className='mr-2' />
                View My Orders
              </Link>
              <Link 
                className='w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200' 
                to={`/${sellerName}`}
              >
                <FaHome className='mr-2' />
                Continue Shopping
              </Link>
            </div>
          </div>
        ) : (
          // Fallback case - when message is null or unknown
          <div className='bg-white rounded-2xl shadow-2xl p-8 text-center border border-gray-100'>
            {/* Debug Header */}
            <div className='mb-6'>
              <div className='inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4'>
                <FaSpinner className='text-gray-500 text-4xl animate-spin' />
              </div>
              <h1 className='text-3xl font-bold text-gray-800 mb-2'>Processing Payment</h1>
              <p className='text-gray-600'>Please wait while we verify your payment</p>
            </div>
            
            <FadeLoader color="#6B7280" className='mx-auto' />
            <p className="text-gray-600 mt-6 text-lg">Verifying payment details...</p>
            
            <div className='mt-6 bg-gray-50 rounded-lg p-4'>
              <p className='text-sm text-gray-700'>
                🔍 Debug Info: Message = {message || 'null'}, Loader = {loader ? 'true' : 'false'}
              </p>
              <p className='text-sm text-gray-700 mt-1'>
                URL: {window.location.href}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConfirmOrder